/* Modulo: Countdown Media — countdown tick + video behavior.
   Server renders the initial timer numbers (no CLS); this just ticks.
   Video: reduced-motion-gated ambient autoplay (pauses off-screen) + external
   YouTube/Vimeo lightbox. Vanilla IIFE — sections can't use window.moduloLazyInit. */
(function () {
  'use strict';
  if (typeof window === 'undefined') return;

  var REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function pad2(n) {
    n = Math.max(0, Math.floor(n));
    return n < 10 ? '0' + n : String(n);
  }

  /* ─── Countdown ─── */
  function initTimer(section) {
    if (section.__cdmTimerInit) return;
    var endAttr = section.getAttribute('data-end-ms');
    var endMs = endAttr ? parseInt(endAttr, 10) : 0;
    var timer = section.querySelector('[data-modulo-cdm-timer]');
    var timerWrap = section.querySelector('[data-modulo-cdm-timer-wrap]');
    if (!endMs || !timer) return;
    section.__cdmTimerInit = true;

    var onComplete = section.getAttribute('data-on-complete') || 'show-message';
    var daysEl    = timer.querySelector('[data-unit="days"]');
    var hoursEl   = timer.querySelector('[data-unit="hours"]');
    var minutesEl = timer.querySelector('[data-unit="minutes"]');
    var secondsEl = timer.querySelector('[data-unit="seconds"]');

    function render(diffMs) {
      var diff = Math.max(0, Math.floor(diffMs / 1000));
      var days = Math.floor(diff / 86400);
      var hrs  = Math.floor((diff % 86400) / 3600);
      var mins = Math.floor((diff % 3600) / 60);
      var secs = diff % 60;
      if (daysEl)    daysEl.textContent    = String(days);
      if (hoursEl)   hoursEl.textContent   = pad2(hrs);
      if (minutesEl) minutesEl.textContent = pad2(mins);
      if (secondsEl) secondsEl.textContent = pad2(secs);
    }

    function complete() {
      section.setAttribute('data-ended', 'true');
      if (onComplete === 'hide-timer' && timerWrap && timerWrap.parentNode) {
        timerWrap.parentNode.removeChild(timerWrap);
      } else if (onComplete === 'hide-section' && section.parentNode) {
        section.parentNode.removeChild(section);
      }
      // show-message handled by CSS via [data-ended="true"]
    }

    var initial = endMs - Date.now();
    if (initial <= 0) { render(0); complete(); return; }
    render(initial);
    section.__cdmInterval = setInterval(function () {
      var diff = endMs - Date.now();
      if (diff <= 0) {
        render(0);
        clearInterval(section.__cdmInterval);
        section.__cdmInterval = null;
        complete();
        return;
      }
      render(diff);
    }, 1000);
  }

  /* ─── External URL parsing (YouTube / Vimeo) ─── */
  function parseVideoUrl(url) {
    if (!url) return null;
    var m;
    m = url.match(/(?:youtube\.com\/(?:watch\?.*v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
    if (m) return { provider: 'youtube', id: m[1] };
    m = url.match(/(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(\d+)/);
    if (m) return { provider: 'vimeo', id: m[1] };
    return null;
  }

  function buildEmbedUrl(parsed) {
    if (!parsed) return '';
    if (parsed.provider === 'youtube') {
      return 'https://www.youtube.com/embed/' + parsed.id + '?autoplay=1&rel=0';
    }
    if (parsed.provider === 'vimeo') {
      return 'https://player.vimeo.com/video/' + parsed.id + '?autoplay=1';
    }
    return '';
  }

  function clearChildren(node) {
    while (node.firstChild) node.removeChild(node.firstChild);
  }

  /* ─── Video (ambient autoplay + external lightbox) ─── */
  function initVideo(section) {
    if (section.__cdmVideoInit) return;
    section.__cdmVideoInit = true;

    /* (1) Ambient autoplaying uploaded video */
    var video = section.querySelector('[data-modulo-cdm-video][data-cdm-autoplay="true"]');
    if (video) {
      if (REDUCE) {
        video.removeAttribute('autoplay');
        video.controls = true;
        try { video.pause(); } catch (e) {}
      } else if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              var p = video.play();
              if (p && p.catch) p.catch(function () {});
            } else {
              video.pause();
            }
          });
        }, { threshold: 0.25 });
        io.observe(video);
        section.__cdmIO = io;
      }
    }

    /* (2) External video lightbox */
    var trigger = section.querySelector('[data-modulo-cdm-lightbox]');
    var modal = section.querySelector('[data-modulo-cdm-lightbox-modal]');
    if (trigger && modal) {
      var body = modal.querySelector('[data-modulo-cdm-lightbox-body]');
      var closeBtn = modal.querySelector('[data-modulo-cdm-lightbox-close]');
      var url = trigger.getAttribute('data-cdm-url');

      function openModal() {
        var parsed = parseVideoUrl(url);
        if (!parsed) return;
        var embed = buildEmbedUrl(parsed);
        if (!embed) return;

        var iframe = document.createElement('iframe');
        iframe.src = embed;
        iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
        iframe.setAttribute('allowfullscreen', '');
        iframe.setAttribute('title', 'Video player');

        clearChildren(body);
        body.appendChild(iframe);
        modal.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        modal.__prevFocus = document.activeElement;
        if (closeBtn) closeBtn.focus();
      }

      function closeModal() {
        modal.setAttribute('aria-hidden', 'true');
        clearChildren(body); // removing the iframe stops playback
        document.body.style.overflow = '';
        if (modal.__prevFocus && modal.__prevFocus.focus) modal.__prevFocus.focus();
      }

      trigger.addEventListener('click', openModal);
      if (closeBtn) closeBtn.addEventListener('click', closeModal);
      modal.addEventListener('click', function (e) {
        if (e.target === modal) closeModal();
      });

      section.__cdmCloseModal = closeModal;
      section.__cdmModal = modal;
    }
  }

  function initSection(section) {
    if (!section) return;
    initTimer(section);
    initVideo(section);
  }

  /* ─── Escape closes any open lightbox ─── */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var open = document.querySelector('.modulo-cdm__lightbox[aria-hidden="false"]');
    if (!open) return;
    var section = open.closest('[data-modulo-cdm]');
    if (section && section.__cdmCloseModal) section.__cdmCloseModal();
  });

  function initAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var els = scope.querySelectorAll('[data-modulo-cdm]');
    for (var i = 0; i < els.length; i++) initSection(els[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  /* ─── Theme editor lifecycle ─── */
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target;
    if (!el) return;
    if (el.matches && el.matches('[data-modulo-cdm]')) {
      initSection(el);
    } else {
      initAll(el);
    }
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var el = e.target;
    if (!el) return;
    var section = (el.matches && el.matches('[data-modulo-cdm]')) ? el : (el.querySelector ? el.querySelector('[data-modulo-cdm]') : null);
    if (!section) return;
    if (section.__cdmInterval) { clearInterval(section.__cdmInterval); section.__cdmInterval = null; }
    if (section.__cdmIO) section.__cdmIO.disconnect();
    if (section.__cdmModal && section.__cdmModal.getAttribute('aria-hidden') === 'false' && section.__cdmCloseModal) {
      section.__cdmCloseModal();
    }
  });
})();
